import { Component } from 'react';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { LicensingPolicy } from '../../../typings/api';
import type { PolicyAction } from '../../../typings/common';
import { window } from '../../globals';
import LicenseModule from '../../redux/licenses';
import PolicyModule from '../../redux/policies';
import type { APIError, IAsyncStatus, LicensingRule } from '../../types';
import { verify_subscription, hasError } from '../../utils';
import Loader from '../generic/Loader';
import EditPolicyCTA from './EditPolicyCTA';
import { RuleItem } from './rules/RuleItem';
import { RuleList } from './rules/RuleList';
import type { NewRule, RuleModalMode } from './rules/RuleModal';
import { RuleModal } from './rules/RuleModal';
import { ErrorAlert } from '../generic';
import type { LicensingRuleType } from '../../../shared/model-typings';
import { customizeableLicensePoliciesFeature } from '../settings/billing/features/consts';

type DisplayLicensePolicyPageProps = {
  params: {
    policyId: number;
  };
  fetchLicenses: (params: { onlyIds: 'true'; pageSize: number }) => Promise<void>;
  createPolicyRule(policyId: number, rule: NewRule): Promise<void>;
  deletePolicyRule(policyId: number, ruleId: number): Promise<void>;
  updatePolicyRule(policyId: number, editedRuleId: number, rule: Partial<LicensingRule>): Promise<void>;
  fetchPolicy: (policyId: number) => Promise<void>;
  updatePolicy(updates: { id: number } & Partial<{ title: string; description: string; defaultAction: PolicyAction }>): Promise<void>;
  policy: LicensingPolicy;
  status: IAsyncStatus<APIError>;
  licenses: LicenseIdOnly[];
  licenseStatus: IAsyncStatus<Error>;
};

type LicenseIdOnly = {
  licenseId: string;
};

type DisplayLicensePolicyPageState = {
  showRuleModal: boolean;
  modalMode: RuleModalMode;
};

class DisplayLicensePolicyPage extends Component<DisplayLicensePolicyPageProps, DisplayLicensePolicyPageState> {
  constructor(props: DisplayLicensePolicyPageProps) {
    super(props);
    this.state = {
      showRuleModal: false,
      modalMode: { editType: 'CREATE', type: 'approved_license' },
    };
  }

  componentDidMount() {
    this.props.fetchPolicy(this.props.params.policyId);
    if (this.props.licenses.length > 0) return;

    this.props.fetchLicenses({
      onlyIds: 'true',
      pageSize: 4999,
    });
  }

  private createRule = (type: LicensingRuleType, defaultRule?: LicensingRule) =>
    verify_subscription('premium')
      ? this.setState({ showRuleModal: true, modalMode: { type, editType: 'CREATE', rule: defaultRule } })
      : window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });

  private editRule = (ruleId: number) =>
    verify_subscription('premium')
      ? this.setState({
        showRuleModal: true,
        modalMode: {
          editType: 'EDIT',
          rule: this.props.policy.rules.find(rule => rule.ruleId === ruleId),
        },
      })
      : window.showPlanSelectorModal({ focusFeature: customizeableLicensePoliciesFeature });

  private hideRuleModal = () => this.setState({ showRuleModal: false });

  private submit = (rule: NewRule, editedRuleId?: number) => {
    if (this.state.modalMode.editType === 'CREATE') {
      this.props.createPolicyRule(this.props.policy.id, rule);
    } else if (this.state.modalMode.editType === 'EDIT') {
      this.props.updatePolicyRule(this.props.policy.id, editedRuleId, rule);
    }
    this.hideRuleModal();
  };

  render() {
    if (!this.props.policy) return <Loader />;
    return (
      <>
        <EditPolicyCTA
          policy={this.props.policy}
          status={this.props.status}
        />
        <div className="container-fluid margin-top">
          <Helmet title={this.props.status.loading ? 'Policy...' : `${this.props.policy.title} - Policies`} />
          {this.props.status.loading ? (
            <Loader />
          ) : (
            <div>
              {hasError(this.props.status) && <ErrorAlert status={this.props.status} />}
              <div className="row">
                {/**
                 * See: https://github.com/fossas/FOSSA/pull/2613 for comments on uncategorized licenses
                 */}
                <div className="col-md-3">
                  <RuleList
                  // Uncategorized should never be the default
                    isDefault={false}
                    policy={this.props.policy}
                    title="Uncategorized"
                  >
                    {
                      (this.props.licenseStatus.loading || !this.props.licenses)
                        ? <Loader />
                        : (
                        this.props.licenses
                          .filter(l => !this.props.policy.rules.some(r => r.licenseId === l.licenseId))
                          .sort((a, b) => a.licenseId.toLowerCase().localeCompare(b.licenseId.toLowerCase()))
                          .map(l => <RuleItem key={l.licenseId} policy={this.props.policy} rule={l} />)
                        )
                    }
                  </RuleList>
                </div>
                <div className="col-md-3">
                  <RuleList
                    isDefault={this.props.policy.latestVersion?.defaultAction === 'DENY'}
                    onRuleAdd={(defaultRule) => this.createRule('denied_license', defaultRule)}
                    onRuleEdit={ruleId => this.editRule(ruleId)}
                    onRuleRemove={this.props.deletePolicyRule.bind(null, this.props.policy.id)}
                    onSetAsDefault={() => this.props.updatePolicy({ id: this.props.policy.id, defaultAction: 'DENY' })}
                    policy={this.props.policy}
                    title="Deny"
                    ruleType="denied_license"
                    bsStyle="danger"
                    icon="times"
                  />
                </div>
                <div className="col-md-3">
                  <RuleList
                    isDefault={this.props.policy.latestVersion?.defaultAction === 'FLAG'}
                    onRuleAdd={(defaultRule) => this.createRule('flagged_license', defaultRule)}
                    onRuleEdit={ruleId => this.editRule(ruleId)}
                    onRuleRemove={this.props.deletePolicyRule.bind(null, this.props.policy.id)}
                    onSetAsDefault={() => this.props.updatePolicy({ id: this.props.policy.id, defaultAction: 'FLAG' })}
                    title="Flag for Review"
                    bsStyle="warning"
                    ruleType="flagged_license"
                    policy={this.props.policy}
                    icon="flag"
                  />
                </div>
                <div className="col-md-3">
                  <RuleList
                    isDefault={this.props.policy.latestVersion?.defaultAction === 'APPROVE'}
                    onRuleAdd={(defaultRule) => this.createRule('approved_license', defaultRule)}
                    onRuleEdit={ruleId => this.editRule(ruleId)}
                    onRuleRemove={this.props.deletePolicyRule.bind(null, this.props.policy.id)}
                    onSetAsDefault={() => this.props.updatePolicy({ id: this.props.policy.id, defaultAction: 'APPROVE' })}
                    title="Approve"
                    ruleType="approved_license"
                    bsStyle="success"
                    policy={this.props.policy}
                    icon="check"
                  />
                </div>
              </div>
            </div>
          )}
          <RuleModal
            show={this.state.showRuleModal}
            mode={this.state.modalMode}
            onClose={this.hideRuleModal}
            onSubmit={this.submit}
          />
        </div>
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  // policies
  policy: PolicyModule.selectors.getCurrentPolicy,
  status: PolicyModule.selectors.getStatus,

  // licenses
  licenses: LicenseModule.selectors.getLicenseList,
  licenseStatus: LicenseModule.selectors.getStatus,
});

const mapDispatchToProps = {
  fetchLicenses: LicenseModule.actions.fetchAll,
  createPolicyRule: PolicyModule.actions.createPolicyRule,
  deletePolicyRule: PolicyModule.actions.deletePolicyRule,
  updatePolicyRule: PolicyModule.actions.updatePolicyRule,
  fetchPolicy: PolicyModule.actions.fetchOne,
  updatePolicy: PolicyModule.actions.update,
};

export default DragDropContext(HTML5Backend)(connect(mapStateToProps, mapDispatchToProps)(DisplayLicensePolicyPage));
